import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Banknote, TrendingUp, TrendingDown, AlertCircle, ArrowRight, PiggyBank,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (pence, currency = 'GBP') => {
  if (pence == null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(pence / 100);
  } catch {
    return `£${(pence / 100).toFixed(0)}`;
  }
};

const fmtDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  try {
    return new Date(yyyymmdd + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return yyyymmdd; }
};

export default function PayoutsTile({ api, locationId, onReconnect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setLoading(true);
    api.get('/dashboard/latest-payout', { params: { location_id: locationId } })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ connected: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, locationId]);

  if (loading) {
    return (
      <Card data-testid="payouts-tile-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-3 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Not connected → CTA
  if (!data?.connected) {
    return (
      <Card className="border-dashed" data-testid="payouts-tile-not-connected">
        <CardContent className="p-5 flex items-start gap-3">
          <Banknote className="w-6 h-6 text-muted-foreground shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">See what actually lands in your bank</p>
            <p className="text-xs text-muted-foreground mt-1">
              Connect Square to track daily payouts, fees and Square Capital loan repayments.
            </p>
          </div>
          <Button size="sm" onClick={onReconnect} data-testid="payouts-tile-connect">Connect</Button>
        </CardContent>
      </Card>
    );
  }

  // Connected but missing PAYOUTS_READ scope → reconnect banner
  if (data.needs_reconnect) {
    return (
      <Card className="border-amber-300 bg-amber-50" data-testid="payouts-tile-needs-reconnect">
        <CardContent className="p-5 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Reconnect Square to unlock daily payout tracking</p>
            <p className="text-xs text-amber-800 mt-1">
              A new permission is needed — one click and you're done.
            </p>
          </div>
          <Button size="sm" onClick={onReconnect} data-testid="payouts-tile-reconnect">Reconnect</Button>
        </CardContent>
      </Card>
    );
  }

  const payout = data.payout;
  const currency = data.currency || 'GBP';

  if (!payout) {
    return (
      <Card data-testid="payouts-tile-empty">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Banknote className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Latest payout</p>
          </div>
          <p className="text-2xl font-bold">—</p>
          <p className="text-xs text-muted-foreground mt-1">No payouts yet. Next arrival will appear here automatically.</p>
        </CardContent>
      </Card>
    );
  }

  const trend = data.trend_pct_vs_same_weekday;
  const trendUp = trend != null && trend >= 0;

  return (
    <Card data-testid="payouts-tile">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Banknote className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Latest payout</p>
          </div>
          {payout.waterfall_mismatch && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" title="Payout amount doesn't match summed entries — flagged for review">
              <AlertCircle className="w-3 h-3 mr-1" /> Check
            </Badge>
          )}
        </div>
        <div>
          <p className="text-3xl font-bold" data-testid="payouts-tile-net">{fmt(payout.net_amount_pence, currency)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Arriving {fmtDate(payout.arrival_date)} · {payout.status}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="payouts-tile-waterfall">
          <Chip label="Gross" value={fmt(payout.gross_pence, currency)} tone="green" testid="chip-gross" />
          <Chip label="Fees" value={fmt(payout.fees_pence, currency)} tone="amber" testid="chip-fees" />
          {payout.loan_repayment_pence > 0 && (
            <Chip label="Loan" value={fmt(payout.loan_repayment_pence, currency)} tone="indigo" testid="chip-loan" />
          )}
          {payout.refunds_pence > 0 && (
            <Chip label="Refunds" value={fmt(payout.refunds_pence, currency)} tone="red" testid="chip-refunds" />
          )}
        </div>
        {payout.loan_repayment_pence > 0 && (
          <p className="text-xs text-indigo-700 flex items-center gap-1" data-testid="payouts-loan-line">
            <PiggyBank className="w-3 h-3" />
            {fmt(payout.loan_repayment_pence, currency)} paid off your Square Capital loan today
          </p>
        )}
        {trend != null && (
          <p className={`text-xs flex items-center gap-1 ${trendUp ? 'text-green-700' : 'text-red-700'}`} data-testid="payouts-trend">
            {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            vs same weekday, last 4 weeks · {trendUp ? '↑' : '↓'} {Math.abs(trend)}%
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Chip({ label, value, tone, testid }) {
  const tones = {
    green: 'bg-green-50 text-green-800 border-green-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone] || tones.slate}`} data-testid={testid}>
      <span className="opacity-70">{label}</span>
      <span>{value}</span>
    </span>
  );
}
